import { events, subscribe } from '@theme/event-bus';

const DESKTOP = '(min-width: 1024px)';
const observers = new WeakMap();

/**
 * Desktop gallery scroller: the column that independently overflows.
 * Falls back to the wrap when the column class is missing.
 * @param {HTMLElement} wrap
 * @returns {HTMLElement}
 */
function getGalleryScroller(wrap) {
  return wrap.closest('.main-product__gallery') ?? wrap;
}

/**
 * @param {HTMLElement} wrap
 * @returns {HTMLButtonElement[]}
 */
function getThumbButtons(wrap) {
  return Array.from(wrap.querySelectorAll('.product-gallery-thumbs__btn'));
}

/**
 * Position (within the thumb list) of the currently active thumbnail.
 * @param {HTMLButtonElement[]} buttons
 */
function getActivePos(buttons) {
  const pos = buttons.findIndex((btn) => btn.getAttribute('aria-current') === 'true');
  return pos === -1 ? 0 : pos;
}

/**
 * Enable/disable the prev/next arrows based on the active thumbnail's position.
 * @param {HTMLElement} wrap
 */
function updateThumbArrows(wrap) {
  const buttons = getThumbButtons(wrap);
  const prev = wrap.querySelector('[data-thumbs-prev]');
  const next = wrap.querySelector('[data-thumbs-next]');
  if (!prev || !next) return;

  const pos = getActivePos(buttons);
  prev.disabled = buttons.length <= 1 || pos <= 0;
  next.disabled = buttons.length <= 1 || pos >= buttons.length - 1;
}

/**
 * @param {HTMLElement} wrap
 */
function setActiveButton(wrap, index) {
  const nav = wrap.querySelector('.product-gallery-thumbs');
  if (!nav) return;

  let activeBtn = null;
  let changed = false;

  nav.querySelectorAll('.product-gallery-thumbs__btn').forEach((btn) => {
    const btnIndex = Number.parseInt(btn.getAttribute('data-gallery-index') ?? '', 10);
    const isActive = btnIndex === index;
    if (isActive) {
      activeBtn = btn;
      changed = btn.getAttribute('aria-current') !== 'true';
    }
    btn.setAttribute('aria-current', isActive ? 'true' : 'false');
  });

  updateThumbArrows(wrap);

  // Keep the active thumbnail visible in the track as the active image changes
  // (e.g. while scrolling a product with many gallery images).
  if (changed && activeBtn) scrollThumbIntoView(wrap, activeBtn);
}

/**
 * Keep the active thumbnail centred within the (horizontally scrollable) track.
 * @param {HTMLElement} wrap
 * @param {HTMLElement} btn
 */
function scrollThumbIntoView(wrap, btn) {
  const track = wrap.querySelector('.product-gallery-thumbs__track');
  if (!track || !btn) return;

  const item = btn.closest('.product-gallery-thumbs__item') ?? btn;
  const trackRect = track.getBoundingClientRect();
  const itemRect = item.getBoundingClientRect();
  const delta = itemRect.left - trackRect.left - (track.clientWidth - itemRect.width) / 2;
  // Absolute target keeps repeated calls idempotent (no overshoot from stacked scrolls).
  const target = track.scrollLeft + delta;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  track.scrollTo({ left: target, behavior: reduceMotion ? 'auto' : 'smooth' });
}

/**
 * Scroll the gallery image matching a thumbnail into view and mark it active.
 * @param {HTMLElement} wrap
 * @param {HTMLElement} btn
 */
function activateThumb(wrap, btn) {
  const index = Number.parseInt(btn.getAttribute('data-gallery-index') ?? '', 10);
  if (Number.isNaN(index)) return;

  const item = wrap.querySelector(`[data-product-gallery-item][data-gallery-index="${index}"]`);
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (item) {
    const scroller = getGalleryScroller(wrap);
    const offset =
      item.getBoundingClientRect().top -
      scroller.getBoundingClientRect().top +
      scroller.scrollTop;
    scroller.scrollTo({ top: offset, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  setActiveButton(wrap, index);
  scrollThumbIntoView(wrap, btn);
}

/**
 * Move the active thumbnail forwards/backwards through the images.
 * @param {HTMLElement} wrap
 * @param {number} direction -1 for previous, 1 for next
 */
function navigateThumb(wrap, direction) {
  const buttons = getThumbButtons(wrap);
  if (buttons.length === 0) return;

  const pos = getActivePos(buttons);
  const nextPos = Math.min(Math.max(pos + direction, 0), buttons.length - 1);
  if (nextPos === pos) return;

  activateThumb(wrap, buttons[nextPos]);
}

/**
 * Pick the gallery slide that occupies the most viewport height (main visible image).
 * @param {HTMLElement} wrap
 */
function syncActiveFromLayout(wrap) {
  if (!window.matchMedia(DESKTOP).matches) return;

  const items = wrap.querySelectorAll('[data-product-gallery-item]');
  if (items.length === 0) return;

  const scroller = getGalleryScroller(wrap);
  const scrollerRect = scroller.getBoundingClientRect();
  let maxVisible = 0;
  let bestIdx = 0;

  items.forEach((el) => {
    const idx = Number.parseInt(el.getAttribute('data-gallery-index') ?? '0', 10);
    // The first item (index 0) is the locked column-one image and has no thumb.
    if (idx === 0) return;

    const r = el.getBoundingClientRect();
    const visible = Math.max(
      0,
      Math.min(r.bottom, scrollerRect.bottom) - Math.max(r.top, scrollerRect.top),
    );
    if (visible > maxVisible) {
      maxVisible = visible;
      bestIdx = Number.isNaN(idx) ? 0 : idx;
    }
  });

  setActiveButton(wrap, bestIdx);
}

/**
 * @param {HTMLElement} wrap
 */
function connectIntersection(wrap) {
  const items = wrap.querySelectorAll('[data-product-gallery-item]');
  if (items.length < 2) return;

  const prev = observers.get(wrap);
  if (prev) prev.disconnect();

  const scroller = window.matchMedia(DESKTOP).matches
    ? getGalleryScroller(wrap)
    : null;

  const io = new IntersectionObserver(
    () => {
      requestAnimationFrame(() => syncActiveFromLayout(wrap));
    },
    { root: scroller, threshold: [0, 0.05, 0.1, 0.25, 0.5, 0.75, 1] },
  );

  items.forEach((el) => io.observe(el));
  observers.set(wrap, io);

  syncActiveFromLayout(wrap);
}

function initProductGalleryThumbs() {
  document.querySelectorAll('.product-gallery-wrap').forEach((wrap) => {
    connectIntersection(wrap);
    updateThumbArrows(wrap);
  });
}

function onThumbArrowClick(event) {
  const arrow = event.target.closest('[data-thumbs-prev], [data-thumbs-next]');
  if (!arrow || !window.matchMedia(DESKTOP).matches) return;

  const wrap = arrow.closest('.product-gallery-wrap');
  if (!wrap) return;

  event.preventDefault();
  navigateThumb(wrap, arrow.hasAttribute('data-thumbs-next') ? 1 : -1);
}

function onThumbClick(event) {
  const btn = event.target.closest('.product-gallery-thumbs__btn');
  if (!btn || !window.matchMedia(DESKTOP).matches) return;

  const wrap = btn.closest('.product-gallery-wrap');
  if (!wrap) return;

  activateThumb(wrap, btn);
}

document.addEventListener('click', onThumbClick);
document.addEventListener('click', onThumbArrowClick);
document.addEventListener('DOMContentLoaded', initProductGalleryThumbs);

window.matchMedia(DESKTOP).addEventListener('change', () => {
  initProductGalleryThumbs();
});

window.addEventListener('resize', () => {
  document.querySelectorAll('.product-gallery-wrap').forEach((wrap) => updateThumbArrows(wrap));
});

subscribe(events.sectionUpdate, () => {
  requestAnimationFrame(() => initProductGalleryThumbs());
});
